import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHead from "@/components/feature/SectionHead";
import { applySeo } from "@/utils/seo";
import { categories } from "@/mocks/categories";
import { articles, featuredArticles, breakingList, getArticle } from "@/mocks/articles";
import { faqs } from "@/mocks/faqs";
import FaqSection from "./components/FaqSection";
import HotRankList from "./components/HotRankList";
import NewsletterForm from "./components/NewsletterForm";

const hero = featuredArticles[0] ?? articles[0];
const coverFeature = getArticle("hot-search-script") ?? articles[1];

export default function Home() {
  useEffect(() => {
    applySeo({
      title: "灼见社 · 独家内幕与深度揭秘 — 最新爆料/幕后真相",
      description:
        "灼见社专注独家内幕、深度揭秘与幕后真相报道。还原娱乐圈、互联网、商业资本与科技背后事件的真实逻辑，提供有分量的内部消息与硬核调查长文。",
      keywords: "最新爆料,独家内幕,深度揭秘,内部消息,幕后真相",
      path: "/",
      type: "website",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "灼见社",
          url: "https://yusr.com.cn/",
          inLanguage: "zh-CN",
          description: "专注独家内幕与深度揭秘的资讯门户。",
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "灼见社",
          url: "https://yusr.com.cn/",
          email: "contact@zhuojianshe.example",
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
  }, []);

  const latestCards = articles
    .filter((a) => a.id !== hero.id && a.id !== coverFeature.id)
    .slice(0, 6);

  const breakingTitles = breakingList.length
    ? breakingList
    : articles.slice(0, 3);

  return (
    <>
      <SiteHeader />
      <main>
        <h1 className="sr-only">
          灼见社——最新爆料 · 独家内幕 · 深度揭秘 · 幕后真相
        </h1>

        {/* breaking strip */}
        <div className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 md:px-6">
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded bg-primary-500 px-2 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-line"></i> 热点追踪
            </span>
            {breakingTitles.map((a, i) => (
              <span key={a.id} className="flex items-center gap-3 text-sm text-foreground-700">
                <Link to={`/article/${a.id}`} className="hover:text-primary-600">
                  {a.title}
                </Link>
                {i < breakingTitles.length - 1 && (
                  <span className="text-foreground-300">/</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* hero + hot ranking */}
        <section className="mx-auto max-w-7xl px-4 pt-8 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <article className="group lg:col-span-2">
              <Link
                to={`/article/${hero.id}`}
                className="relative block overflow-hidden rounded-lg"
                aria-label={hero.title}
              >
                <img
                  src={hero.cover}
                  alt={hero.alt}
                  className="h-[300px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 md:h-[420px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                      {hero.tag}
                    </span>
                    <span className="text-xs text-white/80">{hero.categoryName}</span>
                  </div>
                  <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-white md:text-[32px]">
                    {hero.title}
                  </h2>
                  <p className="mt-3 hidden max-w-2xl text-sm leading-6 text-white/85 md:line-clamp-2">
                    {hero.deck}
                  </p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-white/75">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-[11px] text-white">
                        {hero.authorInitial}
                      </span>
                      {hero.authorName}
                    </span>
                    <span>{hero.publishDate}</span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line"></i> {hero.readMinutes} 分钟阅读
                    </span>
                  </div>
                </div>
              </Link>
            </article>

            <aside className="rounded-lg border border-background-200/70 bg-background-100 px-3 py-4">
              <div className="mb-2 flex items-center gap-2 px-2">
                <i className="ri-fire-fill text-accent-500"></i>
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  大家都在读
                </h2>
              </div>
              <HotRankList />
            </aside>
          </div>
        </section>

        {/* categories */}
        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <SectionHead
            overline="COVERAGE"
            title="六大栏目，各有真相"
            desc="从文娱场上的排位博弈，到互联网与商业资本桌下的暗流，我们按领域梳理值得深挖的内部消息与幕后真相。"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/channel/${c.slug}`}
                className="group relative block overflow-hidden rounded-lg"
                aria-label={`${c.name}栏目`}
              >
                <img
                  src={c.cover}
                  alt={c.alt}
                  className="h-40 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <h3 className="font-heading text-lg font-bold text-white">
                    {c.name}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-white/80">
                    {c.desc}
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-300">
                    进入栏目 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* latest grid */}
        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <SectionHead
            overline="LATEST"
            title="最新独家，正在发生"
            desc="持续更新的内部消息与深度长文，第一时间呈现事件背后尚未公开的部分。"
            linkText="浏览更多独家"
            linkTo="/channel/ent"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latestCards.map((a) => (
              <ArticleCard key={a.id} article={a} showDeck />
            ))}
          </div>
        </section>

        {/* cover feature band */}
        <section className="mt-14 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
              <Link
                to={`/article/${coverFeature.id}`}
                className="group block overflow-hidden rounded-lg"
                aria-label={coverFeature.title}
              >
                <img
                  src={coverFeature.cover}
                  alt={coverFeature.alt}
                  className="h-64 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 md:h-80"
                  loading="lazy"
                />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                    本刊重磅
                  </span>
                  <span className="text-xs text-foreground-500">{coverFeature.categoryName}</span>
                </div>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  <Link to={`/article/${coverFeature.id}`} className="transition-colors hover:text-primary-600">
                    {coverFeature.title}
                  </Link>
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                  {coverFeature.deck}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {coverFeature.tags.slice(0, 4).map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/article/${coverFeature.id}`}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  阅读全文
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* credibility band */}
        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="grid grid-cols-1 gap-6 rounded-lg border border-background-200/70 bg-background-100 p-6 md:grid-cols-4 md:p-8">
            <div className="md:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                一份值得被信赖的深度内容
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                灼见社坚持"先核实、再报道"，以知情人士视角还原真相的同时，恪守边界与分寸。我们相信，真正有分量的独家，不靠耸动的标题，而靠经得起推敲的事实。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg bg-accent-100 p-5 text-center">
              <span className="font-heading text-3xl font-black text-accent-700">6</span>
              <span className="mt-1 text-xs text-accent-900">大垂直栏目</span>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg bg-background-50 p-5 text-center ring-1 ring-background-200">
              <span className="font-heading text-3xl font-black text-primary-600">48h</span>
              <span className="mt-1 text-xs text-foreground-600">线索首轮核验周期</span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <SectionHead
                overline="FAQ"
                title="关于灼见社"
                desc="关于我们的采编原则、线索提供方式与订阅服务的常见疑问，这里都能找到答案。"
                linkText="查看完整介绍"
                linkTo="/about"
              />
            </div>
            <div className="lg:col-span-2">
              <FaqSection />
            </div>
          </div>
        </section>

        {/* subscribe */}
        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="relative overflow-hidden rounded-lg bg-primary-600 px-6 py-10 md:px-10 md:py-14">
            <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-accent-400/20 blur-2xl"></div>
            <div className="pointer-events-none absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-background-50/10 blur-2xl"></div>
            <div className="relative">
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                别让真相只在热搜上停留
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-background-100">
                留下邮箱，每周为你精选值得一读的独家内幕与深度揭秘，第一时间送达。绝不打扰，可随时退订。
              </p>
              <div className="mt-6">
                <NewsletterForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}